import React, { useEffect, useRef, useState } from "react";
import { BsLink45Deg } from "react-icons/bs";
import { RiShoppingBagLine } from "react-icons/ri";
import { Tooltip } from "antd";
import { StaticCompnents } from "../../Utils/StaticComponent";
import {
  formatPostDate,
  addHttpToLink,
} from "../../Utils/StayUpToDate/RenderComponent";
import {
  isProductPlatformName,
  shouldShowContentShopBag,
} from "../../Utils/helpers/carouselHelper";
import { resolvePostShopUrl } from "../../Utils/normalizeShopUrl";
import "./PostMetadata.css";

interface PostMetadataProps {
  post: any;
  type?: string;
  showMenu?: boolean;
}

const getScrollableParent = (node: HTMLElement | null): HTMLElement => {
  let current = node?.parentElement || null;
  while (current) {
    const { overflowY, overflowX } = window.getComputedStyle(current);
    const isScrollable = /(auto|scroll|overlay)/.test(overflowY + overflowX);
    if (isScrollable && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return document.body;
};

/** Prefer absolute http(s) URLs for icon hrefs; skip bare `/…` paths. */
function absoluteHttpHref(raw: unknown): string {
  const href = addHttpToLink(String(raw || "").trim());
  if (!href) return "";
  if (/^https?:\/\//i.test(href)) return href;
  return "";
}

const PostMetadata: React.FC<PostMetadataProps> = ({
  post,
  type,
  showMenu,
}) => {
  const {
    post_date,
    platform,
    permalink,
    shop_link,
    external_link,
    product_image,
  } = post;

  const [dateTooltipOpen, setDateTooltipOpen] = useState(false);
  const dateSpanRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!dateTooltipOpen || !dateSpanRef.current) return;

    const scrollParent = getScrollableParent(dateSpanRef.current);
    const root = scrollParent === document.body ? null : scrollParent;
    let initialized = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!initialized) {
          initialized = true;
          return;
        }
        if (!entry.isIntersecting) setDateTooltipOpen(false);
      },
      { root, threshold: 0 }
    );
    observer.observe(dateSpanRef.current);

    return () => observer.disconnect();
  }, [dateTooltipOpen]);

  // Platform icons mapping
  const platformIcons: any = {
    ShopMy: StaticCompnents().ShopMyIcon,
    Amazon: StaticCompnents().AmazonIcon,
    LTK: StaticCompnents().LTKIcon,
    instagram: StaticCompnents().InstagramIcon,
    pinterest: StaticCompnents().PinterestIcon,
    twitter: StaticCompnents().TwitterIcon,
    tiktok: StaticCompnents().TiktokIcon,
    newsletter: StaticCompnents().NewsletterIcon,
    podcast: StaticCompnents().PodcastIcon,
    linkedin: StaticCompnents().LinkedinIcon,
    youtube: StaticCompnents().YoutubeIcon,
  };

  const renderPlatformIcon = (platform: string) => {
    const IconComponent = platformIcons[platform];
    if (IconComponent) {
      // Special handling for Twitter icon to ensure proper styling
      if (platform === "twitter") {
        return (
          <img
            src="/assets/icons8-twitterx-30.png"
            className="twitter-icon"
            alt="X (Twitter)"
          />
        );
      }
      // Let PostMetadata.css own width/height (incl. responsive breakpoints).
      // preserveAspectRatio keeps viewBox mapping centered in Safari.
      return (
        <IconComponent
          preserveAspectRatio="xMidYMid meet"
          focusable="false"
        />
      );
    }
    return null;
  };

  // Product platforms (Amazon/LTK/…) use the platform icon as the shop link.
  // Shoppable IG/TikTok/etc. often have `product_image` too — that must NOT
  // hide the bag (create/edit modal regression) or treat them as product cards.
  const isProductPlatform = isProductPlatformName(platform);
  const platformUsesCommerce =
    isProductPlatform || Boolean(String(product_image || "").trim());

  // Products: one commerce URL on the platform icon (shop → external → tagged).
  // Content: platform icon stays on the source permalink unless a product still
  // is present (legacy: platform icon then points at shop_link).
  const commerceUrl = resolvePostShopUrl(post);
  const platformLinkRaw = platformUsesCommerce
    ? commerceUrl || shop_link
    : permalink;
  const platformHref = absoluteHttpHref(platformLinkRaw);

  // Bag on content posts with shop_link — independent of product_image.
  const isShoppable = shouldShowContentShopBag(platform, shop_link);
  const shopHref = absoluteHttpHref(shop_link);

  // Extra chain icon only for content rows with a distinct external URL.
  const externalHref = absoluteHttpHref(external_link);
  const hasExternalLink =
    !isProductPlatform &&
    Boolean(externalHref) &&
    externalHref !== platformHref &&
    externalHref !== shopHref;

  const isMiscBrandIcon =
    platform === "MISC-content" ||
    platform === "MISC" ||
    platform === "MISC-product" ||
    String(platform || "").toLowerCase().startsWith("misc");

  const hasValidDate = post_date && new Date(post_date).getFullYear() > 1900;

  return (
    <div
      className="post-metadata-overlay"
      style={showMenu ? { width: "70%" } : {}}
    >
      <div className="post-metadata-content">
        {/* Date */}
        <span className="post-metadata-date">
          {post_date && (
            <Tooltip
              title={hasValidDate ? formatPostDate(post_date) : ""}
              placement="topLeft"
              trigger="hover"
              open={dateTooltipOpen}
              onOpenChange={(visible) =>
                setDateTooltipOpen(visible && !!hasValidDate)
              }
              destroyTooltipOnHide
              getPopupContainer={(triggerNode) =>
                getScrollableParent(triggerNode)
              }
            >
              <span className="post-metadata-date" ref={dateSpanRef}>
                {hasValidDate && <>{formatPostDate(post_date)}</>}
              </span>
            </Tooltip>
          )}
        </span>

        {/* Icons container */}
        <div className="post-metadata-icons">
          {/* Platform Icon — products: commerce URL only; content: permalink */}
          {isMiscBrandIcon ? (
            platformHref ? (
              <a
                href={platformHref}
                target="_blank"
                rel="noopener noreferrer"
                className="post-metadata-icon-link"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src="/assets/Socials_OffWhite.png"
                  className="search-bubble-icon"
                  alt="Icon"
                  style={{ border: "none" }}
                />
              </a>
            ) : (
              <span
                className="post-metadata-icon-link"
                aria-disabled
                style={{ cursor: "default", pointerEvents: "none" }}
              >
                <img
                  src="/assets/Socials_OffWhite.png"
                  className="search-bubble-icon"
                  alt="Icon"
                  style={{ border: "none" }}
                />
              </span>
            )
          ) : (
            <>
              {platform && platformHref ? (
                <a
                  href={platformHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="post-metadata-icon-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderPlatformIcon(platform)}
                </a>
              ) : (
                platform && (
                  <span
                    className="post-metadata-icon-link"
                    aria-disabled
                    style={{ cursor: "default", pointerEvents: "none" }}
                  >
                    {renderPlatformIcon(platform)}
                  </span>
                )
              )}
            </>
          )}

          {/* Shop Icon (bag) — content posts with a shop_link only */}
          {isShoppable && shopHref && (
            <a
              href={shopHref}
              target="_blank"
              rel="noopener noreferrer"
              className="post-metadata-icon-link"
              onClick={(e) => e.stopPropagation()}
            >
              <RiShoppingBagLine
                preserveAspectRatio="xMidYMid meet"
                focusable="false"
              />
            </a>
          )}

          {/* External Link Icon — content only when distinct from platform/shop */}
          {hasExternalLink && (
            <a
              href={externalHref}
              target="_blank"
              rel="noopener noreferrer"
              className="post-metadata-icon-link"
              onClick={(e) => e.stopPropagation()}
            >
              <BsLink45Deg
                preserveAspectRatio="xMidYMid meet"
                focusable="false"
              />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostMetadata;
